
import { Input } from "@/components/ui/Input";
import { Check, Edit2, Loader2, Save, X } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { useSceneStore } from "../stores/sceneStore";
import { useEditMode } from "../hooks/useEditMode";
import { useSaveSpace } from "../hooks/useSaveSpace";
import { EditModeToggle } from "./EditModeToggle";
import { ViewPerspectiveSwitcher } from "./ViewPerspectiveSwitcher";
import { ResetViewButton } from "./ResetViewButton";
import { SoundtrackControls } from "./SoundtrackControls";
import { useIsMobile } from "@/lib/useMediaQuery";
import { cn } from "@/lib/utils";

/** Delay before auto-hiding the floating toolbar on mobile (ms) */
const MOBILE_HIDE_DELAY = 3000;

/**
 * Floating toolbar with editable world name (top-left) and camera controls (top-right).
 * On mobile: auto-hides after 3s of inactivity, reappears on touch/click.
 */
export function FloatingToolbar() {
  const worldName = useSceneStore((s) => s.worldName);
  const setWorldName = useSceneStore((s) => s.setWorldName);
  const { isReadonly } = useEditMode();
  const { saveStatus, canSave, handleSave: saveSpace } = useSaveSpace();
  const isMobile = useIsMobile();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(worldName);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mobile auto-hide state
  const [visible, setVisible] = useState(true);
  const hideTimerRef = useRef<number | null>(null);

  // Reset the auto-hide timer (called on user interaction)
  const resetTimer = useCallback(() => {
    setVisible(true);
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    if (isMobile) {
      hideTimerRef.current = window.setTimeout(() => {
        setVisible(false);
      }, MOBILE_HIDE_DELAY);
    }
  }, [isMobile]);

  // Set up auto-hide timer on mount (mobile only)
  useEffect(() => {
    if (!isMobile) {
      setVisible(true);
      return;
    }

    // Start the initial hide timer
    resetTimer();

    const handleInteraction = () => resetTimer();

    document.addEventListener("pointerdown", handleInteraction);
    document.addEventListener("touchstart", handleInteraction, {
      passive: true,
    });

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      document.removeEventListener("pointerdown", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
    };
  }, [isMobile, resetTimer]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (isReadonly) return;
    setEditValue(worldName);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed) {
      setWorldName(trimmed);
    } else {
      setEditValue(worldName); // Revert if empty
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(worldName);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <div
      className={cn(
        "absolute top-2 md:top-4 left-2 md:left-4 right-2 md:right-4 z-20",
        "flex items-start justify-between pointer-events-none",
        "transition-opacity duration-300 ease-in-out",
        visible ? "opacity-100" : "opacity-0 pointer-events-none",
      )}
    >
      {/* Left: World name + Soundtrack controls */}
      <div
        className={cn(
          "pointer-events-auto flex items-center gap-1 md:gap-2",
          // On mobile, stack vertically when soundtrack controls are present
          isMobile && "flex-col items-start",
        )}
      >
        {/* World name */}
        {isEditing && !isReadonly ? (
          <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-sands-200 rounded-lg px-3 py-2 shadow-sm">
            <Input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              accent="none"
              surface="ghost"
              borderWeight="none"
              inputSize="sm"
              className="font-medium w-40 px-0 py-0"
              maxLength={50}
            />
            <button
              onClick={handleSave}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center text-healing-500 hover:text-healing-600 transition-colors"
              title="Save"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center text-sands-400 hover:text-sands-600 transition-colors"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={handleStartEdit}
            className={cn(
              "group flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-sands-200 rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 shadow-sm transition-colors",
              isReadonly
                ? "cursor-default"
                : "hover:border-healing-300 cursor-text",
            )}
            title={isReadonly ? worldName : "Click to edit world name"}
            disabled={isReadonly}
          >
            <span className="text-sm font-medium text-sands-800 truncate max-w-[120px] md:max-w-none">
              {worldName}
            </span>
            {!isReadonly && (
              <Edit2 className="w-3.5 h-3.5 text-sands-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            )}
          </button>
        )}

        {/* Soundtrack controls - placed below space name on mobile, right on desktop */}
        <SoundtrackControls />
      </div>

      {/* Right: Save + Edit mode toggle + Camera controls + Reset */}
      <div
        className={cn(
          "pointer-events-auto flex items-center gap-1 md:gap-2",
          // On mobile, wrap items to prevent overflow
          isMobile && "flex-wrap justify-end",
        )}
      >
        {/* Save button — visible only in editing mode when there are unsaved changes */}
        {!isReadonly && canSave && (
          <button
            onClick={saveSpace}
            disabled={saveStatus === "saving"}
            className={cn(
              "flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors min-h-[44px] backdrop-blur-sm shadow-sm",
              saveStatus === "saved"
                ? "bg-white/90 border border-success/30 text-success"
                : "bg-white/90 border border-sands-200 text-sands-600 hover:text-sands-800 hover:border-sands-300",
            )}
            title="Save space changes"
          >
            {saveStatus === "saving" ? (
              <Loader2 size={16} className="animate-spin" />
            ) : saveStatus === "saved" ? (
              <Check size={16} />
            ) : (
              <Save size={16} />
            )}
            <span className="hidden sm:inline">
              {saveStatus === "saving"
                ? "Saving..."
                : saveStatus === "saved"
                  ? "Saved"
                  : "Save"}
            </span>
          </button>
        )}
        <EditModeToggle />
        <ViewPerspectiveSwitcher />
        <ResetViewButton />
      </div>
    </div>
  );
}
